import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'secret-teaching-favorites';

function readFavorites(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((i) => typeof i === 'string') : [];
  } catch {
    return [];
  }
}

export function useFavorites() {
  const [favorites, setFavorites] = useState<string[]>(() => readFavorites());

  useEffect(() => {
    const handler = () => setFavorites(readFavorites());
    window.addEventListener('storage', handler);
    window.addEventListener('favorites-updated', handler);
    return () => {
      window.removeEventListener('storage', handler);
      window.removeEventListener('favorites-updated', handler);
    };
  }, []);

  const persist = useCallback((next: string[]) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    setFavorites(next);
    window.dispatchEvent(new Event('favorites-updated'));
  }, []);

  const toggleFavorite = useCallback(
    (id: string) => {
      const current = readFavorites();
      const next = current.includes(id)
        ? current.filter((i) => i !== id)
        : [...current, id];
      persist(next);
      return next.includes(id);
    },
    [persist],
  );

  const isFavorite = useCallback((id: string) => favorites.includes(id), [favorites]);

  return { favorites, toggleFavorite, isFavorite };
}